import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  FormAnswerMediaFilterInputObject,
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskCompletion,
  TaskCompletionFilterInputObject,
} from 'graphql-common';
import {
  useTaskCompletionsIdsLazyQuery,
  useTaskTimelineCompletionsCountersQuery,
} from '@lib/graphql-common';
import useTaskCompletionsSelectionActions from '@lib/components/TaskCompletions/utils/useTaskCompletionsSelectionActions';
import { useTableItemsBulkSelectWithAllIds } from '@lib/hooks/useTableItemsBulkSelect';
import { DEFAULT_LIST_PARAMS, GetUrlParams, ListParams } from '@lib/enums/urls';
import { logger } from '@lib/services/logger';
import useTaskCompletionsQueryHook from '@lib/utils/fetch/hooks/useTaskCompletionsQueryHook';
import { TaskCompletionsTab } from '@lib/components/TaskCompletions/utils/constants';
import getTaskCompletionsTableColumns, {
  StatusChangeHandler,
} from '@lib/components/TaskCompletions/utils/getTaskCompletionsTableColumns';
import getTaskCompletionsTableActionsMenu from '@lib/components/TaskCompletions/utils/getTaskCompletionsTableActionsMenu';
import getTaskCompletionsListActionsForSelected from '@lib/components/TaskCompletions/utils/getTaskCompletionsListActionsForSelected';
import {
  TaskCompletionsModalActions,
  TaskCompletionsModalUrls,
} from '../types';

const VALIDITY_HINT_BY_TAB: Partial<
  Record<TaskCompletionsTab, FormTemplateQuestionFieldOptionsValidityHintEnum>
> = {
  [TaskCompletionsTab.Alert]:
    FormTemplateQuestionFieldOptionsValidityHintEnum.Failure,
  [TaskCompletionsTab.Warning]:
    FormTemplateQuestionFieldOptionsValidityHintEnum.Warning,
  [TaskCompletionsTab.Success]:
    FormTemplateQuestionFieldOptionsValidityHintEnum.Success,
};

const getValidityHintFilter = (
  validityHint: FormTemplateQuestionFieldOptionsValidityHintEnum,
): TaskCompletionFilterInputObject => ({
  validityHint: { predicate: { eq: validityHint } },
});

export default function useTaskCompletionsActions({
  task,
  completionFilters,
  formAnswerFilters,
  initialCompletionId,
  initialBulkCompareIds,
  detailOnly,
  hideCompletionDetails,
  onClose,
  urls,
  actions,
}: {
  task: Task | null;
  // Narrow the list, every tab counter, "select all" and the media count —
  // see useTaskCompletionsScope. Keep both referentially stable.
  completionFilters: TaskCompletionFilterInputObject[];
  formAnswerFilters: FormAnswerMediaFilterInputObject[];
  // Closes the modal before a row action navigates away — the host may stay
  // mounted (e.g. Intervention view -> another intervention), which would
  // otherwise leave the modal open over the new page.
  onClose: () => void;
  urls: TaskCompletionsModalUrls;
  // Host-provided write actions (status update, create intervention); the
  // matching column control / menu items are dropped when omitted.
  actions?: TaskCompletionsModalActions;
  // Lets a caller open the modal straight to a specific completion's detail
  // view (e.g. a "view" row action elsewhere) instead of landing on the list.
  initialCompletionId?: string | null;
  // Lets a caller open the modal straight into the bulk-compare view for a
  // pre-selected set of completions (e.g. a bulk "Compare completions"
  // action elsewhere) instead of landing on the list.
  initialBulkCompareIds?: string[] | null;
  // The list is unreachable (no back arrow), so nothing consumes the
  // completions page or the tab counters — don't fetch them.
  detailOnly?: boolean;
  // Drops every way into the detail view: row click, the "Completion
  // details" menu item and the "Compare completions" bulk action.
  hideCompletionDetails?: boolean;
}) {
  const { t } = useTranslation();
  const groupKey = task?.groupKey;
  const skip = !groupKey || !!detailOnly;

  const [activeTab, setActiveTab] = useState<TaskCompletionsTab>(
    TaskCompletionsTab.All,
  );
  const [paginationParams, setPaginationParams] =
    useState<ListParams>(DEFAULT_LIST_PARAMS);
  const [selectedCompletionId, setSelectedCompletionId] = useState<
    string | null
  >(initialCompletionId ?? null);

  const validityHint = VALIDITY_HINT_BY_TAB[activeTab];
  const additionalFilters = useMemo<TaskCompletionFilterInputObject[]>(
    () =>
      validityHint
        ? [...completionFilters, getValidityHintFilter(validityHint)]
        : completionFilters,
    [validityHint, completionFilters],
  );

  const [fetchCompletionIdsQuery] = useTaskCompletionsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // No `page`/`limit` — resolves every id matching the current tab's filter,
  // not just the current page, so "select all" can materialize the full set.
  const fetchCompletionIds = useCallback(
    () =>
      fetchCompletionIdsQuery({
        variables: {
          groupKey: groupKey || '',
          filters: additionalFilters.length ? additionalFilters : undefined,
        },
      }),
    [fetchCompletionIdsQuery, groupKey, additionalFilters],
  );

  const useBulkSelectResult = useTableItemsBulkSelectWithAllIds<TaskCompletion>(
    { fetchIds: fetchCompletionIds },
  );

  const onTabChange = (tab: TaskCompletionsTab) => {
    setActiveTab(tab);
    setPaginationParams((prev) => ({ ...prev, [GetUrlParams.Page]: '1' }));
    useBulkSelectResult.handleUnselectAllTableItems();
  };

  const {
    collection,
    error: completionsError,
    firstLoading: completionsFirstLoading,
    loading: completionsLoading,
    refetch,
    totalCount,
  } = useTaskCompletionsQueryHook({
    groupKey: groupKey || '',
    skip,
    additionalFilters,
    fetchPolicy: 'network-only',
    paginationParams,
  });

  const { data: countersData, error: countersError } =
    useTaskTimelineCompletionsCountersQuery({
      variables: {
        groupKey: groupKey || '',
        filters: completionFilters,
        alertFilters: [
          ...completionFilters,
          getValidityHintFilter(
            FormTemplateQuestionFieldOptionsValidityHintEnum.Failure,
          ),
        ],
        warningFilters: [
          ...completionFilters,
          getValidityHintFilter(
            FormTemplateQuestionFieldOptionsValidityHintEnum.Warning,
          ),
        ],
        successFilters: [
          ...completionFilters,
          getValidityHintFilter(
            FormTemplateQuestionFieldOptionsValidityHintEnum.Success,
          ),
        ],
        formAnswerFilters,
      },
      skip,
      fetchPolicy: 'network-only',
    });

  // Total for the whole list scope (unlike `totalCount`, which is
  // scoped to the active tab) — used to gate the "compare" bulk action,
  // which needs at least two completions to pick from in the first place.
  // Should the counters query fail, fall back to the active tab's total so
  // the action isn't silently hidden.
  const totalCompletionsCount = _get(
    countersData,
    'all.metadata.totalCount',
    countersError ? totalCount : 0,
  );
  const {
    bulkCompareIds,
    setBulkCompareIds,
    onCompareSelected,
    onViewIntervention,
    onCreateIntervention,
    onCreateInterventionBulk,
  } = useTaskCompletionsSelectionActions({
    groupKey,
    collection,
    selectedItems: useBulkSelectResult.selectedTableItems,
    getInterventionUrl: urls.getInterventionUrl,
    onCreateInterventionForSelected: actions?.onCreateInterventionForSelected,
    onBeforeNavigate: onClose,
    initialBulkCompareIds,
  });

  // Reset to a fresh view every time the modal opens for a (possibly
  // different) task, rather than carrying over the previous open's tab,
  // page and selection. Done during render so the first paint of a reopened
  // modal never shows the previous open's state.
  const prevGroupKeyRef = useRef(groupKey);
  if (prevGroupKeyRef.current !== groupKey) {
    prevGroupKeyRef.current = groupKey;
    if (groupKey) {
      setActiveTab(TaskCompletionsTab.All);
      setPaginationParams(DEFAULT_LIST_PARAMS);
      setSelectedCompletionId(initialCompletionId ?? null);
      setBulkCompareIds(initialBulkCompareIds ?? null);
      useBulkSelectResult.handleUnselectAllTableItems();
    }
  }

  useEffect(() => {
    if (groupKey) logger.info('TaskCompletionsModal: opened', { groupKey });
  }, [groupKey]);

  const onViewCompletion = useCallback((item?: TaskCompletion) => {
    if (item?.id) setSelectedCompletionId(item.id);
  }, []);

  const onChangeSelectedCompletionId = (id: string) => {
    if (id) setSelectedCompletionId(id);
  };

  const onBackToList = () => {
    setSelectedCompletionId(null);
    setBulkCompareIds(null);
    useBulkSelectResult.handleUnselectAllTableItems();
  };

  // Each tab's value doubles as its label key and its alias in the counters
  // query.
  const tabs = Object.values(TaskCompletionsTab).map((value) => ({
    value,
    label: t(value),
    counter: _get(countersData, `${value}.metadata.totalCount`),
  }));

  const onStatusChange = actions?.onStatusChange;
  const isStatusUpdating = !!actions?.isStatusUpdating;
  const handleStatusChange = useMemo<StatusChangeHandler | undefined>(() => {
    if (!onStatusChange) return undefined;
    return (completion, status) => {
      // The host surfaces its own errors — only refetch once it succeeds.
      Promise.resolve(onStatusChange(completion, status)).then(
        () => refetch(),
        () => undefined,
      );
    };
  }, [onStatusChange, refetch]);

  const tableColumns = useMemo(
    () =>
      getTaskCompletionsTableColumns({
        t,
        activeTab,
        onStatusChange: handleStatusChange,
        isStatusUpdating,
      }),
    [t, activeTab, handleStatusChange, isStatusUpdating],
  );

  const actionsMenu = useMemo(
    () =>
      getTaskCompletionsTableActionsMenu({
        t,
        onViewCompletion: hideCompletionDetails ? undefined : onViewCompletion,
        onCreateIntervention,
        onViewIntervention,
      }),
    [
      t,
      onViewCompletion,
      onCreateIntervention,
      onViewIntervention,
      hideCompletionDetails,
    ],
  );

  const tableActionsForSelected = getTaskCompletionsListActionsForSelected({
    t,
    onCreateInterventionSelected: onCreateInterventionBulk,
    onCompareSelected: hideCompletionDetails ? undefined : onCompareSelected,
    selectedItemsCount: useBulkSelectResult.selectedTableItems.length,
    canCompare: totalCompletionsCount > 1,
  });
  // Row checkboxes only exist to feed the bulk actions.
  const hasBulkActions = !hideCompletionDetails || !!onCreateInterventionBulk;

  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );

  return {
    activeTab,
    onTabChange,
    tabs,
    tableColumns,
    tableData: collection,
    tableLoading: completionsLoading || isStatusUpdating,
    tableFirstLoading: completionsFirstLoading,
    tableError: completionsError,
    totalCount,
    paginationParams,
    setPaginationParams,
    orderingField,
    orderingDirection,
    actionsMenu,
    tableActionsForSelected,
    hasBulkActions,
    useBulkSelectResult,
    selectedCompletionId,
    onChangeSelectedCompletionId,
    onBackToList,
    bulkCompareIds,
    onViewCompletion: hideCompletionDetails ? undefined : onViewCompletion,
  };
}
